import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Lock, Unlock, RefreshCw, Banknote, Coins, AlertTriangle,
  CheckCircle2, History, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const METHOD_LABELS = {
  cash: 'Cash',
  credit_card_terminal: 'Credit (Terminal)',
  debit_card_terminal: 'Debit (Terminal)',
  credit_card_online: 'Credit (Online)',
  paypal: 'PayPal',
  cheque: 'Cheque',
  etransfer: 'e-Transfer',
  gift_card: 'Gift Card',
};

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

const todayIso = () => new Date().toISOString().slice(0, 10);

export const DailyRegisterPanel = ({ currentUserRole, onRegisterChange }) => {
  const [date, setDate] = useState(todayIso());
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actualCash, setActualCash] = useState('');
  const [notes, setNotes] = useState('');
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reopenPin, setReopenPin] = useState('');
  const [reopening, setReopening] = useState(false);

  const fetchState = useCallback(async (targetDate) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/daily-register`, {
        params: { date: targetDate },
        withCredentials: true,
      });
      setState(res.data);
      if (res.data?.register?.actual_cash != null) {
        setActualCash(String(res.data.register.actual_cash));
        setNotes(res.data.register.notes || '');
      } else {
        setActualCash('');
        setNotes('');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load register');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/daily-register/history`, {
        params: { limit: 30 },
        withCredentials: true,
      });
      setHistory(res.data?.items || []);
    } catch (e) {
      // Non-fatal
    }
  }, []);

  useEffect(() => { fetchState(date); }, [date, fetchState]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const expected = state?.expected || {};
  const register = state?.register;
  const isClosed = !!state?.is_closed;
  const expectedCash = Number(expected.expected_cash || 0);
  const actualNum = parseFloat(actualCash);
  const variance = useMemo(() => {
    if (Number.isNaN(actualNum)) return null;
    return Math.round((actualNum - expectedCash) * 100) / 100;
  }, [actualNum, expectedCash]);

  const submitClose = async () => {
    if (Number.isNaN(actualNum) || actualNum < 0) {
      toast.error('Enter a valid Actual Cash amount'); return;
    }
    if (!pin || pin.length < 4) {
      toast.error('Enter your 4-digit PIN'); return;
    }
    if (!window.confirm(`Close register for ${date}?\n\nExpected cash: ${fmt(expectedCash)}\nActual cash: ${fmt(actualNum)}\nVariance: ${variance >= 0 ? '+' : ''}${fmt(variance)}\n\nNew in-store sales will be blocked until the register is reopened.`)) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/payments/daily-register/close`, {
        date, actual_cash: actualNum, notes, pin,
      }, { withCredentials: true });
      toast.success(`Register closed for ${date}`);
      setPin('');
      await fetchState(date);
      await fetchHistory();
      onRegisterChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to close register');
    } finally {
      setSubmitting(false);
    }
  };

  const submitReopen = async () => {
    if (!reopenPin || reopenPin.length < 4) {
      toast.error('Enter your 4-digit PIN'); return;
    }
    if (!window.confirm(`Reopen the register for ${date}? Sales will resume for this date.`)) return;
    setReopening(true);
    try {
      await axios.post(`${API_URL}/api/admin/payments/daily-register/reopen`, {
        date, pin: reopenPin,
      }, { withCredentials: true });
      toast.success(`Register reopened for ${date}`);
      setReopenPin('');
      await fetchState(date);
      await fetchHistory();
      onRegisterChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reopen register');
    } finally {
      setReopening(false);
    }
  };

  const varianceColor = variance == null
    ? 'text-[#6B6B7A]'
    : Math.abs(variance) < 0.005
      ? 'text-[#06D6A0]'
      : variance > 0
        ? 'text-[#F59E0B]'
        : 'text-[#FF3B6B]';

  const isAdmin = (currentUserRole || '').toLowerCase() === 'admin';

  if (loading && !state) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#6366F1]" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="register-panel">
      {/* Header row */}
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs text-[#6B6B7A]">Register date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={todayIso()}
              className="bg-white max-w-[180px] mt-1"
              data-testid="register-date-input"
            />
          </div>
          <Button variant="outline" onClick={() => fetchState(date)} className="rounded-full" data-testid="register-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm ${
          isClosed ? 'bg-[#FF3B6B]/10 text-[#FF3B6B] border border-[#FF3B6B]/30'
                   : 'bg-[#06D6A0]/10 text-[#058B69] border border-[#06D6A0]/30'
        }`} data-testid="register-status-pill">
          {isClosed ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
          {isClosed ? 'Register Closed' : 'Register Open'}
        </div>
      </div>

      {/* Closed-state info banner */}
      {isClosed && register && (
        <div className="bg-gradient-to-r from-[#FF3B6B]/10 to-[#FF7A9C]/10 border border-[#FF3B6B]/30 rounded-2xl p-4 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="register-closed-banner">
          <div className="flex items-center gap-2 text-[#FF3B6B] font-semibold">
            <Lock className="w-5 h-5" />
            Closed by {register.closed_by_user_name || '—'}
          </div>
          <div className="text-xs text-[#6B6B7A]">
            at {new Date(register.closed_at).toLocaleString()}
          </div>
          <div className="text-xs text-[#1E1E24] font-semibold">
            New in-store sales for {register.date} are blocked.
          </div>
        </div>
      )}

      {/* 4-card summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4">
          <div className="flex items-center gap-2 text-xs text-[#6B6B7A]"><Banknote className="w-3.5 h-3.5" /> Expected Cash</div>
          <p className="text-2xl font-bold text-[#1E1E24] mt-1" data-testid="register-expected-cash">{fmt(expectedCash)}</p>
          <p className="text-[11px] text-[#6B6B7A] mt-1">Computed from ledger</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4">
          <div className="flex items-center gap-2 text-xs text-[#6B6B7A]"><Coins className="w-3.5 h-3.5" /> Actual Cash</div>
          {isClosed ? (
            <p className="text-2xl font-bold text-[#1E1E24] mt-1" data-testid="register-actual-cash-readonly">{fmt(register?.actual_cash)}</p>
          ) : (
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={actualCash}
              onChange={(e) => setActualCash(e.target.value)}
              className="text-2xl font-bold mt-1 h-auto px-1 py-0.5 border-0 border-b-2 border-[#6366F1]/40 rounded-none bg-transparent focus:ring-0"
              data-testid="register-actual-cash-input"
            />
          )}
          <p className="text-[11px] text-[#6B6B7A] mt-1">Counted from drawer</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4">
          <div className="flex items-center gap-2 text-xs text-[#6B6B7A]"><AlertTriangle className="w-3.5 h-3.5" /> Variance</div>
          <p className={`text-2xl font-bold mt-1 ${varianceColor}`} data-testid="register-variance">
            {isClosed
              ? `${(register?.variance ?? 0) >= 0 ? '+' : ''}${fmt(register?.variance)}`
              : variance == null
                ? '—'
                : `${variance >= 0 ? '+' : ''}${fmt(variance)}`}
          </p>
          <p className="text-[11px] text-[#6B6B7A] mt-1">Actual − Expected</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4">
          <div className="flex items-center gap-2 text-xs text-[#6B6B7A]"><FileText className="w-3.5 h-3.5" /> Net Today</div>
          <p className="text-2xl font-bold text-[#1E1E24] mt-1" data-testid="register-net">{fmt(expected.net)}</p>
          <p className="text-[11px] text-[#6B6B7A] mt-1">
            {expected.payment_count || 0} payments · {expected.refund_count || 0} refunds
          </p>
        </div>
      </div>

      {/* By-method breakdown */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
        <p className="text-sm font-semibold text-[#1E1E24] mb-3">Net by Payment Method</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          {Object.entries(expected.totals_by_method || {}).map(([method, amt]) => (
            <div key={method} className="flex items-center justify-between p-2 rounded-lg bg-[#FFFDF8] border border-[#E8E5DF]" data-testid={`register-method-${method}`}>
              <span className="text-[#6B6B7A] text-xs">{METHOD_LABELS[method] || method}</span>
              <span className="font-semibold text-[#1E1E24]">{fmt(amt)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Close-out form (when open) */}
      {!isClosed && (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-4" data-testid="register-close-form">
          <p className="text-sm font-semibold text-[#1E1E24]">Close Register</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[#1E1E24] text-xs">Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Short by $1.50 — recounted twice"
                rows={3}
                className="mt-1 rounded-xl border-[#E8E5DF]"
                data-testid="register-notes-input"
              />
            </div>
            <div>
              <Label className="text-[#1E1E24] text-xs">Employee PIN *</Label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                placeholder="• • • •"
                className="mt-1 rounded-xl border-[#E8E5DF] tracking-widest text-center"
                data-testid="register-pin-input"
              />
              <p className="text-[11px] text-[#6B6B7A] mt-1">Variance and closing are signed by the PIN holder.</p>
            </div>
          </div>
          <Button
            onClick={submitClose}
            disabled={submitting || !pin}
            className="bg-[#FF3B6B] hover:bg-[#e0325a] text-white rounded-full"
            data-testid="register-close-btn"
          >
            <Lock className="w-4 h-4 mr-2" />
            {submitting ? 'Closing…' : `Close Register for ${date}`}
          </Button>
        </div>
      )}

      {/* Reopen form (when closed and user is admin) */}
      {isClosed && isAdmin && (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-3" data-testid="register-reopen-form">
          <p className="text-sm font-semibold text-[#1E1E24]">Reopen Register (admin only)</p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-[#1E1E24] text-xs">PIN *</Label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={reopenPin}
                onChange={(e) => setReopenPin(e.target.value.replace(/\D/g, ''))}
                placeholder="• • • •"
                className="mt-1 rounded-xl border-[#E8E5DF] tracking-widest text-center max-w-[160px]"
                data-testid="register-reopen-pin-input"
              />
            </div>
            <Button
              onClick={submitReopen}
              disabled={reopening || !reopenPin}
              className="bg-[#06D6A0] hover:bg-[#05b88a] text-white rounded-full"
              data-testid="register-reopen-btn"
            >
              <Unlock className="w-4 h-4 mr-2" />
              {reopening ? 'Reopening…' : 'Reopen Register'}
            </Button>
            <p className="text-[11px] text-[#6B6B7A]">Audit-logged. Use only when corrections are needed.</p>
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden" data-testid="register-history">
        <div className="flex items-center gap-2 px-5 pt-4 pb-3 border-b border-[#E8E5DF]">
          <History className="w-4 h-4 text-[#6B6B7A]" />
          <p className="text-sm font-semibold text-[#1E1E24]">Recent Closures</p>
          <span className="text-xs text-[#6B6B7A]">({history.length})</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Expected</TableHead>
              <TableHead className="text-right">Actual</TableHead>
              <TableHead className="text-right">Variance</TableHead>
              <TableHead className="text-right">Net Sales</TableHead>
              <TableHead>Closed by</TableHead>
              <TableHead>When</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-[#6B6B7A] py-6">No closures recorded yet.</TableCell></TableRow>
            ) : history.map((r) => {
              const v = Number(r.variance || 0);
              const vCol = Math.abs(v) < 0.005 ? 'text-[#06D6A0]' : v > 0 ? 'text-[#F59E0B]' : 'text-[#FF3B6B]';
              const statusBadge = r.status === 'closed'
                ? <span className="px-2 py-0.5 rounded-full bg-[#FF3B6B]/10 text-[#FF3B6B] text-[11px] font-semibold">CLOSED</span>
                : <span className="px-2 py-0.5 rounded-full bg-[#06D6A0]/10 text-[#058B69] text-[11px] font-semibold">REOPENED</span>;
              return (
                <TableRow key={r.register_id} data-testid={`register-history-row-${r.date}`}>
                  <TableCell className="font-semibold text-[#1E1E24]">{r.date}</TableCell>
                  <TableCell>{statusBadge}</TableCell>
                  <TableCell className="text-right">{fmt(r.expected_cash)}</TableCell>
                  <TableCell className="text-right">{fmt(r.actual_cash)}</TableCell>
                  <TableCell className={`text-right font-semibold ${vCol}`}>
                    {v >= 0 ? '+' : ''}{fmt(v)}
                    {Math.abs(v) < 0.005 && <CheckCircle2 className="w-3 h-3 inline ml-1" />}
                  </TableCell>
                  <TableCell className="text-right">{fmt(r.net)}</TableCell>
                  <TableCell className="text-xs">{r.closed_by_user_name || '—'}</TableCell>
                  <TableCell className="text-xs text-[#6B6B7A]">
                    {r.closed_at ? new Date(r.closed_at).toLocaleString() : '—'}
                    {r.reopened_at && <div className="text-[10px] text-[#06D6A0]">reopened {new Date(r.reopened_at).toLocaleString()}</div>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default DailyRegisterPanel;
